import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  FilePdf,
  ShareNetwork,
  Trash,
  Wrench,
} from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { gmao, gmaoInstallationWorkflow } from "@/api";
import * as quotesApi from "@/api/quotes";
import InstallationArchiveModal from "@/components/gmao/InstallationArchiveModal";
import InstallationContactActions from "@/components/gmao/InstallationContactActions";
import InstallationDeliveryValidationCard from "@/components/gmao/InstallationDeliveryValidationCard";
import InstallationRefusalModal from "@/components/gmao/InstallationRefusalModal";
import InstallationPreparationChecklist from "@/components/gmao/InstallationPreparationChecklist";
import InstallationStatusCards from "@/components/gmao/InstallationStatusCards";
import InstallationScheduleForm from "@/components/gmao/InstallationScheduleForm";
import ShareModal from "@/components/ShareModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/api";
import {
  INSTALLATION_STATUS,
  isInstallationInProgress,
  normalizeInstallationStatus,
} from "@/lib/gmaoInstallationStatus";


const EMPTY = Object.freeze({});


function formatDateTime(value) {
  if (!value) return "Non renseigné";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}


function requestErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return fallback;
  const formatted = formatApiError(detail);
  return formatted && formatted !== "Une erreur est survenue." ? formatted : fallback;
}


function personName(person = {}) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ")
    || person.email
    || "Non renseigné";
}


function openBlobInNewTab(blob, revokeAfterMs = 30000) {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), revokeAfterMs);
}


export default function GmaoInstallationDetail() {
  const { installationId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const schedulingRef = useRef(false);

  const [detail, setDetail] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [documentBusy, setDocumentBusy] = useState("");
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [refusalOpen, setRefusalOpen] = useState(false);
  const [refusalReason, setRefusalReason] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [archiveDependencies, setArchiveDependencies] = useState({});
  const [archiveReason, setArchiveReason] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [releaseMachineReservation, setReleaseMachineReservation] = useState(false);
  const [cancelLinkedIntervention, setCancelLinkedIntervention] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDetail(await gmao.getInstallation(installationId));
    } catch (requestError) {
      const message = requestErrorMessage(requestError, "Impossible de charger cette installation.");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [installationId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    gmaoInstallationWorkflow.getOptions()
      .then((payload) => {
        if (active) setAssignees(payload?.assignees || []);
      })
      .catch(() => {
        if (active) setAssignees([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const installation = detail || EMPTY;
  const order = detail?.order || EMPTY;
  const quote = detail?.quote || EMPTY;
  const contact = detail?.contact || EMPTY;
  const machine = detail?.machine || EMPTY;
  const intervention = detail?.intervention || EMPTY;
  const deliveryNote = detail?.deliveryNote || EMPTY;
  const bankDeliveryApproval = detail?.bankDeliveryApproval || EMPTY;
  const deliveryValidation = detail?.deliveryValidation || EMPTY;
  const status = normalizeInstallationStatus(order.installationStatus || detail?.status);

  const machineName = machine.name
    || installation.machineName
    || order.machineName
    || quote.machineName
    || "Machine non renseignée";

  const clientName = contact.company || personName(contact);
  const assignee = assignees.find((item) => item.id === (installation.assignedUserId || installation.technicianId));

  const draftInstallation = useMemo(
    () => ({
      ...installation,
      serialNumber: installation.serialNumber || machine.serialNumber || "",
      siteName: installation.siteName || machine.siteName || contact.siteName || contact.company || "",
      address: installation.address || machine.address || contact.address || "",
      city: installation.city || machine.city || contact.city || "",
      postalCode: installation.postalCode || machine.postalCode || contact.postalCode || contact.postal_code || "",
      country: installation.country || machine.country || contact.country || "FR",
      accessInstructions: installation.accessInstructions || machine.accessInstructions || "",
      implementationNotes: installation.implementationNotes || machine.installationNotes || "",
      notes: installation.notes || order.notes || machine.notes || "",
    }),
    [installation, machine, contact, order.notes]
  );

  const saveDraft = async (payload) => {
    setBusy(true);
    try {
      const updated = await gmaoInstallationWorkflow.savePreparationDraft(installationId, payload);
      setDetail((current) => current ? { ...current, ...updated } : updated);
      toast.success("Information enregistrée.");
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible d’enregistrer cette information."));
      throw requestError;
    } finally {
      setBusy(false);
    }
  };

  const finalizePreparation = async () => {
    setBusy(true);
    try {
      await gmao.preparePurchaseOrderInstallation(installationId, {
        machine_name: machineName,
        serial_number: draftInstallation.serialNumber,
        installation_date: draftInstallation.desiredDate,
        site_name: draftInstallation.siteName,
        address: draftInstallation.address,
        city: draftInstallation.city,
        postal_code: draftInstallation.postalCode,
        country: draftInstallation.country || "FR",
        notes: draftInstallation.notes || "",
        implementation_notes: draftInstallation.implementationNotes || "",
        access_instructions: draftInstallation.accessInstructions || "",
        maintenance_enabled: draftInstallation.maintenanceEnabled !== false,
        maintenance_interval_months: draftInstallation.maintenanceIntervalMonths || 12,
        maintenance_warning_days: draftInstallation.maintenanceWarningDays ?? 60,
      });
      toast.success("Préparation terminée. Vous pouvez générer le bon de livraison.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de terminer la préparation."));
    } finally {
      setBusy(false);
    }
  };

  const generateDeliveryNote = async () => {
    setDocumentBusy("generate");
    try {
      await gmao.generateInstallationDeliveryNote(installationId);
      toast.success("Bon de livraison généré.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de générer le bon de livraison."));
    } finally {
      setDocumentBusy("");
    }
  };

  const uploadDeliveryNote = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!(file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"))) {
      toast.error("Le bon de livraison doit être au format PDF.");
      return;
    }
    setDocumentBusy("upload");
    try {
      await gmao.uploadInstallationDeliveryNote(installationId, file);
      toast.success("Bon de livraison ajouté.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible d’ajouter le bon de livraison."));
    } finally {
      setDocumentBusy("");
    }
  };

  const openDeliveryNote = async () => {
    setDocumentBusy("open");
    try {
      openBlobInNewTab(await gmao.getInstallationDeliveryNoteBlob(installationId));
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible d’ouvrir le bon de livraison."));
    } finally {
      setDocumentBusy("");
    }
  };

  const onShareGenerated = async (share) => {
    if (!share?.id) return;
    try {
      await gmaoInstallationWorkflow.registerDeliveryShare(installationId, share.id);
      toast.success("Bon envoyé : attente de la confirmation du client.");
      setShareOpen(false);
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Partage créé mais impossible de l’associer à l’installation."));
    }
  };

  const acceptReschedule = async () => {
    setBusy(true);
    try {
      await gmaoInstallationWorkflow.acceptClientReschedule(installationId);
      toast.success("Nouvelle date reprise. Une nouvelle confirmation client est attendue.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de reprendre la nouvelle date."));
    } finally {
      setBusy(false);
    }
  };

  const recordClientAcceptance = async () => {
    setBusy(true);
    try {
      await gmaoInstallationWorkflow.recordClientDecision(
        installationId,
        { decision: "accept" }
      );
      toast.success("Acceptation du client enregistrée.");
      await load();
    } catch (requestError) {
      toast.error(
        requestErrorMessage(
          requestError,
          "Impossible d’enregistrer l’acceptation du client."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const recordClientRefusal = async () => {
    if (!refusalReason.trim()) return;

    setBusy(true);
    try {
      await gmaoInstallationWorkflow.recordClientDecision(
        installationId,
        {
          decision: "refuse",
          reason: refusalReason.trim(),
        }
      );
      toast.success("Refus enregistré. L’installation est revenue dans À préparer.");
      setRefusalOpen(false);
      setRefusalReason("");
      await load();
    } catch (requestError) {
      toast.error(
        requestErrorMessage(
          requestError,
          "Impossible d’enregistrer le refus du client."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const startAfterConfirmation = async () => {
    setBusy(true);
    try {
      await gmaoInstallationWorkflow.startAfterConfirmation(installationId);
      toast.success("Livraison confirmée. Installation prête à planifier.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de préparer la planification de l’installation."));
    } finally {
      setBusy(false);
    }
  };

  const returnToConfirmation = async () => {
    if (!returnReason.trim() || busy) return;
    setBusy(true);
    try {
      await gmaoInstallationWorkflow.returnToConfirmation(installationId, returnReason.trim());
      setReturnOpen(false);
      setReturnReason("");
      toast.success("Installation revenue en attente de confirmation.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de revenir à l’étape précédente."));
    } finally {
      setBusy(false);
    }
  };

  const returnToPlanning = async () => {
    if (!returnReason.trim() || busy) return;
    setBusy(true);
    try {
      await gmaoInstallationWorkflow.returnToPlanning(installationId, returnReason.trim());
      setReturnOpen(false);
      setReturnReason("");
      toast.success("Intervention annulée. Installation revenue à Prête à installer.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de revenir à la planification."));
    } finally {
      setBusy(false);
    }
  };

  const scheduleInstallation = async (payload) => {
    // Verrou synchrone : un second submit ne doit pas partir avant le re-render.
    if (schedulingRef.current) return;
    schedulingRef.current = true;
    setBusy(true);
    try {
      await gmao.scheduleInstallation(installationId, payload);
      toast.success("Installation planifiée. L’intervention est disponible.");
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "Impossible de planifier l’installation."));
    } finally {
      schedulingRef.current = false;
      setBusy(false);
    }
  };

  const openArchive = async () => {
    setArchiveOpen(true);
    setArchiveLoading(true);
    setArchiveReason("");
    setArchiveDependencies({});
    setReleaseMachineReservation(false);
    setCancelLinkedIntervention(false);

    try {
      const dependencies =
        await gmaoInstallationWorkflow.getArchiveDependencies(
          installationId
        );
      setArchiveDependencies(dependencies || {});
    } catch (requestError) {
      setArchiveOpen(false);
      toast.error(
        requestErrorMessage(
          requestError,
          "Impossible de vérifier les éléments liés à cette installation."
        )
      );
    } finally {
      setArchiveLoading(false);
    }
  };

  const submitArchive = async () => {
    if (!archiveReason.trim()) return;

    setArchiveSaving(true);
    try {
      await gmaoInstallationWorkflow.archiveInstallation(
        installationId,
        {
          reason: archiveReason.trim(),
          release_machine_reservation: releaseMachineReservation,
          cancel_intervention: cancelLinkedIntervention,
        }
      );
      toast.success("Installation archivée dans Terminées.");
      setArchiveOpen(false);
      navigate("/app/gmao/installations");
    } catch (requestError) {
      toast.error(requestErrorMessage(
        requestError,
        "Impossible d’archiver cette installation."
      ));
    } finally {
      setArchiveSaving(false);
    }
  };

  const openQuote = async () => {
    if (!quote.id) return;
    try {
      openBlobInNewTab(await quotesApi.downloadPdf(quote.id));
    } catch {
      toast.error("Impossible d’ouvrir le devis.");
    }
  };

  if (loading) {
    return <main className="p-6 text-sm text-[var(--do-muted)]">Chargement de l’installation…</main>;
  }

  if (error || !detail) {
    return (
      <main className="p-6">
        <div className="lirah-card p-6" role="alert">
          <strong>Installation indisponible.</strong>
          <p className="mt-2 text-sm text-[var(--do-muted)]">{error}</p>
        </div>
      </main>
    );
  }

  const confirmationStatus = installation.deliveryConfirmationStatus || "not_sent";

  return (
    <main className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8">
      <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link to="/app/gmao/installations" className="inline-flex items-center gap-2 text-sm font-bold text-[var(--do-accent)]">
            <ArrowLeft size={16} /> Installations
          </Link>
          <div className="mt-3 label-eyebrow">{installation.reference || "Installation"}</div>
          <h1 className="mt-1 font-heading text-2xl font-black text-[var(--do-text)] sm:text-3xl">{machineName}</h1>
          <p className="mt-1 text-sm text-[var(--do-muted)]">{clientName}</p>
        </div>

        {status !== INSTALLATION_STATUS.ARCHIVED ? (
          <Button
            type="button"
            variant="actaGhost"
            className="text-[var(--do-rose)] hover:text-[var(--do-rose)]"
            disabled={busy || archiveSaving}
            onClick={openArchive}
            data-testid="installation-archive-open"
          >
            <Trash size={17} />
            Supprimer l’installation
          </Button>
        ) : null}
      </header>

      <InstallationStatusCards status={status} />

      {status === INSTALLATION_STATUS.ARCHIVED ? (
        <section
          className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
          data-testid="installation-archive-summary"
        >
          <div className="label-eyebrow">Installation archivée</div>
          <p className="mt-2 text-sm font-bold text-[var(--do-text)]">
            {installation.archiveReason || "Motif non renseigné"}
          </p>
          <p className="mt-1 text-xs text-[var(--do-muted)]">
            Archivée le {formatDateTime(installation.archivedAt)}
          </p>
        </section>
      ) : null}

      <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.75fr)] xl:items-start">
        <div className="space-y-5">
          {status === INSTALLATION_STATUS.PENDING ? (
            <>
              {installation.deliveryConfirmationStatus === "rejected"
                && installation.deliveryRefusalReason ? (
                <section
                  className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
                  data-testid="installation-client-refusal-summary"
                >
                  <div className="label-eyebrow">Refus client</div>
                  <p className="mt-2 text-sm font-bold text-[var(--do-text)]">
                    {installation.deliveryRefusalReason}
                  </p>
                </section>
              ) : null}

              <InstallationPreparationChecklist
              installation={draftInstallation}
              saving={busy}
              onSave={saveDraft}
              onFinalize={finalizePreparation}
            />
            </>
          ) : (
            <section className="lirah-card p-5 sm:p-6">
              <div className="label-eyebrow">Installation</div>
              <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-text)]">Informations validées</h2>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <div><dt className="label-eyebrow">Numéro de série</dt><dd className="mt-1 text-sm font-semibold">{draftInstallation.serialNumber || "Non renseigné"}</dd></div>
                <div><dt className="label-eyebrow">Date de livraison</dt><dd className="mt-1 text-sm font-semibold">{formatDateTime(draftInstallation.desiredDate)}</dd></div>
                <div><dt className="label-eyebrow">Site</dt><dd className="mt-1 text-sm font-semibold">{draftInstallation.siteName || "Non renseigné"}</dd></div>
                {status !== INSTALLATION_STATUS.READY_FOR_DELIVERY && assignee ? (
                  <div><dt className="label-eyebrow">Intervenant planifié</dt><dd className="mt-1 text-sm font-semibold">{personName(assignee)}</dd></div>
                ) : null}
                <div className="sm:col-span-2"><dt className="label-eyebrow">Adresse</dt><dd className="mt-1 text-sm font-semibold">{[draftInstallation.address, draftInstallation.postalCode, draftInstallation.city].filter(Boolean).join(" · ") || "Non renseignée"}</dd></div>
                <div className="sm:col-span-2"><dt className="label-eyebrow">Accès</dt><dd className="mt-1 whitespace-pre-wrap text-sm font-semibold">{draftInstallation.accessInstructions || "Non renseigné"}</dd></div>
                <div className="sm:col-span-2"><dt className="label-eyebrow">Implantation</dt><dd className="mt-1 whitespace-pre-wrap text-sm font-semibold">{draftInstallation.implementationNotes || "Non renseignée"}</dd></div>
              </dl>
            </section>
          )}

          {(status === INSTALLATION_STATUS.PENDING
            || status === INSTALLATION_STATUS.READY_FOR_DELIVERY) ? (
            <InstallationDeliveryValidationCard
              installationId={installationId}
              approval={bankDeliveryApproval}
              validation={deliveryValidation}
              legacy={Boolean(detail.legacyInstallation)}
              disabled={busy}
              onChanged={load}
            />
          ) : (
            <section className="lirah-card p-5 sm:p-6" data-testid="installation-delivery-validation-summary">
              <div className="label-eyebrow">Validation livraison</div>
              <h2 className="mt-1 font-heading text-lg font-bold text-[var(--do-text)]">
                Dossier de livraison
              </h2>
              <p className="mt-2 text-sm text-[var(--do-muted)]">
                {deliveryValidation.ready
                  ? "Les éléments obligatoires du dossier sont complets."
                  : "Certains éléments du dossier de livraison restent à compléter."}
              </p>
              {!deliveryValidation.ready && Array.isArray(deliveryValidation.checks) && (
                <p className="mt-2 text-xs text-[var(--do-muted)]">
                  Éléments manquants : {deliveryValidation.checks
                    .filter((check) => check.required && !check.ok)
                    .map((check) => check.label).join(", ") || "vérification nécessaire"}
                </p>
              )}
              {deliveryNote.id && (
                <Button type="button" className="mt-4" variant="actaSecondary" disabled={Boolean(documentBusy)} onClick={openDeliveryNote}>
                  <FilePdf size={17} /> Consulter le bon de livraison
                </Button>
              )}
            </section>
          )}
        </div>

        <aside className="space-y-5">
          <InstallationContactActions contact={contact} />

          <section className="lirah-card p-5" data-testid="installation-delivery-note-workflow">
            <div className="label-eyebrow">Bon de livraison</div>
            <h2 className="mt-1 font-heading text-lg font-black text-[var(--do-text)]">
              {deliveryNote.id ? "Document prêt" : "Document à générer"}
            </h2>

            {status === INSTALLATION_STATUS.PENDING && (
              <p className="mt-2 text-sm text-[var(--do-muted)]">Terminez d’abord la préparation guidée.</p>
            )}

            {status === INSTALLATION_STATUS.READY_FOR_DELIVERY && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={uploadDeliveryNote}
                />

                {!deliveryNote.id ? (
                  <div className="mt-4 space-y-2">
                    <Button variant="actaPrimary" className="w-full" disabled={Boolean(documentBusy)} onClick={generateDeliveryNote}>
                      <FilePdf size={17} /> {documentBusy === "generate" ? "Génération…" : "Générer le bon de livraison"}
                    </Button>
                    <Button className="w-full" variant="actaSecondary" disabled={Boolean(documentBusy)} onClick={() => fileInputRef.current?.click()}>
                      Importer un PDF
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    <Button className="w-full" variant="actaSecondary" disabled={Boolean(documentBusy)} onClick={openDeliveryNote}>
                      Voir le bon de livraison
                    </Button>
                    <Button className="w-full" variant="actaSecondary" disabled={Boolean(documentBusy)} onClick={() => setShareOpen(true)}>
                      <ShareNetwork size={17} /> Envoyer via partage sécurisé
                    </Button>
                  </div>
                )}

                {deliveryNote.id ? (
                  <div
                    className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
                    data-testid="installation-client-confirmation"
                  >
                    <div className="text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
                      Confirmation client
                    </div>

                    {confirmationStatus === "pending" ? (
                      <p className="mt-1 text-sm font-bold text-[var(--do-text)]">
                        En attente de réponse du client
                      </p>
                    ) : null}

                    {confirmationStatus === "not_sent" ? (
                      <p className="mt-1 text-sm font-bold text-[var(--do-text)]">
                        Indiquer la réponse du client
                      </p>
                    ) : null}

                    {(confirmationStatus === "pending" || confirmationStatus === "not_sent") ? (
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <Button
                          type="button"
                          variant="actaPrimary"
                          disabled={busy}
                          onClick={recordClientAcceptance}
                          data-testid="installation-client-accept"
                        >
                          Client accepte
                        </Button>
                        <Button
                          type="button"
                          variant="actaDanger"
                          disabled={busy}
                          onClick={() => {
                            setRefusalReason("");
                            setRefusalOpen(true);
                          }}
                          data-testid="installation-client-refuse"
                        >
                          Client refuse
                        </Button>
                      </div>
                    ) : null}

                    {confirmationStatus === "confirmed" && (
                      <>
                        <p className="mt-1 text-sm font-bold text-[var(--do-accent)]">✓ Livraison validée par le client</p>
                        <p className="mt-1 text-xs text-[var(--do-muted)]">{formatDateTime(installation.deliveryConfirmedAt)}</p>
                        <Button variant="actaPrimary" className="mt-3 w-full" disabled={busy} onClick={startAfterConfirmation} data-testid="installation-start-after-confirmation">
                          Passer l’installation en cours
                        </Button>
                      </>
                    )}

                    {confirmationStatus === "reschedule_requested" && (
                      <>
                        <p className="mt-1 text-sm font-bold text-[var(--do-text)]">Nouvelle date proposée par le client</p>
                        <p className="mt-1 text-sm">{formatDateTime(installation.deliveryProposedDate)}</p>
                        {installation.deliveryClientMessage && (
                          <p className="mt-2 text-xs text-[var(--do-muted)]">{installation.deliveryClientMessage}</p>
                        )}
                        <Button className="mt-3 w-full" variant="actaSecondary" disabled={busy} onClick={acceptReschedule}>
                          Reprendre cette date
                        </Button>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            )}

            {status === INSTALLATION_STATUS.READY_FOR_INSTALLATION && !intervention.id && (
              <section className="lirah-card p-5" data-testid="installation-return-to-confirmation">
                <h2 className="font-heading text-lg font-bold text-[var(--do-text)]">Revenir à l’étape précédente</h2>
                <p className="mt-2 text-sm text-[var(--do-muted)]">
                  Retourner en attente de confirmation de livraison sans supprimer les documents ni la machine réservée.
                </p>
                {!returnOpen ? (
                  <Button className="mt-3" type="button" variant="actaSecondary" disabled={busy} onClick={() => setReturnOpen(true)}>
                    Revenir à Attente confirmation
                  </Button>
                ) : (
                  <div className="mt-3 grid gap-3">
                    <label className="grid gap-1 text-sm font-semibold text-[var(--do-text)]">
                      Motif obligatoire
                      <Textarea className="rounded-[10px] border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
                        maxLength={1000} rows={3} value={returnReason}
                        onChange={(event) => setReturnReason(event.target.value)} />
                    </label>
                    <p className="text-sm text-[var(--do-muted)]">Confirmez ce retour. L’action sera enregistrée dans l’historique.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="actaPrimary" disabled={busy || !returnReason.trim()} onClick={returnToConfirmation}>
                        Confirmer le retour
                      </Button>
                      <Button type="button" variant="actaSecondary" disabled={busy} onClick={() => { setReturnOpen(false); setReturnReason(""); }}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {status === INSTALLATION_STATUS.READY_FOR_INSTALLATION && !intervention.id && (
              <InstallationScheduleForm
                assignees={assignees.filter((person) => ["technician", "maintenance_manager"].includes(person.role) && person.status !== "disabled")}
                saving={busy}
                onSubmit={scheduleInstallation}
              />
            )}

            {status === INSTALLATION_STATUS.SCHEDULED && intervention.id && (
              <section className="lirah-card p-5" data-testid="installation-return-to-planning">
                <h2 className="font-heading text-lg font-bold text-[var(--do-text)]">Revenir à la planification</h2>
                <p className="mt-2 text-sm text-[var(--do-muted)]">
                  L’intervention planifiée sera annulée et conservée dans l’historique. La machine restera réservée.
                </p>
                {!returnOpen ? (
                  <Button type="button" className="mt-3" variant="actaSecondary" disabled={busy}
                    onClick={() => setReturnOpen(true)}>
                    Modifier la planification
                  </Button>
                ) : (
                  <div className="mt-3 grid gap-3">
                    <label className="grid gap-1 text-sm font-semibold text-[var(--do-text)]">
                      Motif obligatoire
                      <Textarea className="rounded-[10px] border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
                        maxLength={1000} rows={3} value={returnReason}
                        onChange={(event) => setReturnReason(event.target.value)} />
                    </label>
                    <p className="text-sm text-[var(--do-muted)]">
                      Confirmez l’annulation de l’intervention liée. Le retour sera journalisé.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="actaPrimary" disabled={busy || !returnReason.trim()} onClick={returnToPlanning}>
                        Confirmer le retour
                      </Button>
                      <Button type="button" variant="actaSecondary" disabled={busy}
                        onClick={() => { setReturnOpen(false); setReturnReason(""); }}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {isInstallationInProgress(status) && (
              <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4">
                <div className="flex items-center gap-2 font-bold text-[var(--do-accent)]">
                  <Wrench size={18} />
                  {status === INSTALLATION_STATUS.READY_FOR_INSTALLATION
                    ? "Prête à installer"
                    : status === INSTALLATION_STATUS.SCHEDULED
                      ? "Installation planifiée"
                      : "Installation en cours"}
                </div>
                <p className="mt-2 text-sm text-[var(--do-muted)]">
                  {status === INSTALLATION_STATUS.READY_FOR_INSTALLATION
                    ? "La livraison a été acceptée. L’intervention terrain reste à planifier."
                    : status === INSTALLATION_STATUS.SCHEDULED
                      ? "L’intervention est planifiée et attend son démarrage."
                      : "L’intervention terrain est en cours. Sa clôture validera la machine installée."}
                </p>
                {intervention.id && (
                  <Button asChild variant="actaPrimary" className="mt-3 w-full">
                    <Link to={`/app/gmao/interventions/${intervention.id}`}>
                      {status === INSTALLATION_STATUS.IN_PROGRESS
                        ? "Ouvrir l’intervention pour valider l’installation"
                        : "Ouvrir l’intervention"}
                    </Link>
                  </Button>
                )}
              </div>
            )}

            {status === INSTALLATION_STATUS.INSTALLED && (
              <div className="mt-4 space-y-1 text-sm font-bold text-[var(--do-accent)]">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} weight="fill" /> Installation terminée
                </div>
                <p className="font-normal text-[var(--do-muted)]">
                  {installation.reportSignedAt
                    ? "Bon d'intervention signé par le client"
                    : "Bon d'intervention : signature client en attente"}
                </p>
              </div>
            )}
          </section>

          <section className="lirah-card p-5">
            <div className="label-eyebrow">Documents associés</div>
            <div className="mt-4 space-y-2">
              <Button className="w-full" variant="actaSecondary" disabled={!quote.id} onClick={openQuote}>
                Voir le devis accepté
              </Button>
              {deliveryNote.id && (
                <Button className="w-full" variant="actaSecondary" disabled={Boolean(documentBusy)} onClick={openDeliveryNote}>
                  Voir le bon de livraison
                </Button>
              )}
            </div>
          </section>
        </aside>
      </div>

      <InstallationRefusalModal
        open={refusalOpen}
        saving={busy}
        reason={refusalReason}
        onReasonChange={setRefusalReason}
        onClose={() => {
          if (!busy) {
            setRefusalOpen(false);
            setRefusalReason("");
          }
        }}
        onSubmit={recordClientRefusal}
      />

      <InstallationArchiveModal
        open={archiveOpen}
        loading={archiveLoading}
        saving={archiveSaving}
        dependencies={archiveDependencies}
        reason={archiveReason}
        releaseMachineReservation={releaseMachineReservation}
        cancelIntervention={cancelLinkedIntervention}
        onReasonChange={setArchiveReason}
        onReleaseMachineChange={setReleaseMachineReservation}
        onCancelInterventionChange={setCancelLinkedIntervention}
        onClose={() => {
          if (!archiveSaving) {
            setArchiveOpen(false);
          }
        }}
        onSubmit={submitArchive}
      />

      {shareOpen && deliveryNote.id && (
        <ShareModal
          item={{
            type: "document",
            id: deliveryNote.id,
            name: deliveryNote.name || "Bon de livraison",
          }}
          defaultProspect={{
            ...contact,
            id: contact.id || installation.prospectId,
          }}
          createShare={(payload) => gmao.shareInstallationDeliveryNote(installationId, payload)}
          lockRecipient
          onGenerated={onShareGenerated}
          onClose={() => setShareOpen(false)}
        />
      )}
    </main>
  );
}
